import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AiShipmentChat from '../features/ai-shipment/AiShipmentPage';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Kicker } from '../components/ui/Kicker';
import { Icon, type IconName } from '../components/ui/Icon';

type LockFeature = { icon: IconName; title: string; body: string };

const FEATURES: LockFeature[] = [
  {
    icon: 'sparkle',
    title: 'Describe, don’t fill',
    body: 'Type a one-line brief. We parse origin, destination, weight and service.',
  },
  {
    icon: 'truck',
    title: 'Live carrier quotes',
    body: 'Aramex, DHL, FedEx, SMSA — side-by-side, against your wallet.',
  },
  {
    icon: 'shield',
    title: 'Confirm before charging',
    body: 'Nothing auto-ships. Every booking needs your explicit approval.',
  },
  {
    icon: 'download',
    title: 'Label returned in seconds',
    body: 'AWB + PDF delivered as soon as the charge clears.',
  },
];

const LockedState: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <Kicker>
            <Icon name="lock" size={11} /> Closed beta · Access required
          </Kicker>
          <PageTitle style={{ marginTop: 8 }}>
            AI shipment <span className="ai-accent">agent.</span>
          </PageTitle>
          <PageSub>
            A conversational way to quote, pay, and ship — currently rolling out to pilot accounts.
          </PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Button variant="ghost" size="md" onClick={() => navigate('/shipping')}>
            <Icon name="package" size={14} />
            <span>Classic flow</span>
          </Button>
          <Button variant="blue" size="md" onClick={() => navigate('/contact')}>
            <Icon name="mail" size={14} />
            <span>Request access</span>
          </Button>
        </div>
      </PageHead>

      {/* ——— Stat-style status row ——— */}
      <section className="ai-stats">
        <div className="ai-stat">
          <div className="ai-stat-label">Access</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value" style={{ fontSize: 22 }}>Invite only</div>
            <Badge tone="warn" dot>Locked</Badge>
          </div>
          <div className="ai-stat-foot">Enabled by your workspace admin.</div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Rollout</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value" style={{ fontSize: 22 }}>Pilot cohort</div>
            <Badge tone="info">Q2 · wave 3</Badge>
          </div>
          <div className="ai-stat-foot">Expanding gradually to accounts.</div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Model</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value" style={{ fontSize: 22 }}>AlmaAI</div>
            <Badge tone="cyan">v2.4</Badge>
          </div>
          <div className="ai-stat-foot">Shipping-tuned, tool-calling.</div>
        </div>
        <div className="ai-stat">
          <div className="ai-stat-label">Turnaround</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value" style={{ fontSize: 22 }}>~ 1 day</div>
            <Badge tone="ok" dot>SLA</Badge>
          </div>
          <div className="ai-stat-foot">From request to enabled.</div>
        </div>
      </section>

      {/* ——— Hero ribbon (same DNA as ov-whatsnew) ——— */}
      <section className="ai-hero">
        <div className="ai-hero-text">
          <Badge tone="cyan" style={{ marginBottom: 12 }}>What’s next · AI agent</Badge>
          <h3>
            Ship a parcel with <em>one sentence.</em>
          </h3>
          <p>
            Our shipment agent reads your brief, quotes live carriers, confirms the charge, and
            produces a printable label — all inside one conversation. The beta is being rolled
            out by invitation.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'var(--brand-cyan)', color: 'var(--brand-navy)' }}
              onClick={() => navigate('/contact')}
            >
              <Icon name="arrowRight" size={13} /> Request access
            </button>
            <button
              type="button"
              className="btn btn-sm"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.18)',
              }}
              onClick={() => navigate('/faq')}
            >
              <Icon name="help" size={13} /> Learn more
            </button>
          </div>
        </div>

        <div className="ai-hero-viz" aria-hidden>
          <div className="ai-hero-chat">
            <div className="ai-hero-head">
              <span className="ai-hero-dot" style={{ background: '#ff5f57' }} />
              <span className="ai-hero-dot" style={{ background: '#febc2e' }} />
              <span className="ai-hero-dot" style={{ background: '#28c840' }} />
              <span className="ai-hero-tag">almaship / agent</span>
            </div>

            <div className="ai-hero-bubble ai-hero-user">
              Ship a 2.3 kg carton from Ramallah → New York, by Thursday.
            </div>
            <div className="ai-hero-bubble ai-hero-ai">
              Found 4 carriers. <strong style={{ color: '#5de6b6' }}>DHL</strong> priority is{' '}
              <strong>$31.30</strong> (3 days).
            </div>

            <div className="ai-hero-rates">
              <div className="ai-hero-rate ai-hero-rate-active">
                <div className="ai-hero-rate-label">DHL · priority</div>
                <div className="ai-hero-rate-value mono">$31.30</div>
              </div>
              <div className="ai-hero-rate">
                <div className="ai-hero-rate-label">FedEx · express</div>
                <div className="ai-hero-rate-value mono">$46.90</div>
              </div>
            </div>

            <div className="ai-hero-bubble ai-hero-user">Go with DHL. Charge wallet.</div>
            <div className="ai-hero-success">
              <Icon name="checkCircle" size={14} color="#5de6b6" />
              <span>Label generated · AWB 4721 90 0023</span>
            </div>
          </div>
        </div>
      </section>

      {/* ——— Feature grid ——— */}
      <section className="ai-feature-grid">
        <div className="ov-card-head ai-feature-head">
          <div>
            <div className="kicker">How it will work</div>
            <div className="ov-card-title">What the agent does for you</div>
          </div>
        </div>
        <div className="ai-features">
          {FEATURES.map((f) => (
            <div key={f.title} className="ai-feature">
              <div className="ai-feature-icon">
                <Icon name={f.icon} size={16} />
              </div>
              <div>
                <div className="ai-feature-title">{f.title}</div>
                <div className="ai-feature-body">{f.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ——— CTA strip ——— */}
      <Card pad className="ai-cta">
        <div className="ai-cta-icon">
          <Icon name="mail" size={18} color="#fff" />
        </div>
        <div className="ai-cta-text">
          <div className="ai-cta-title">Ready to try the AI agent?</div>
          <div className="ai-cta-sub">
            Contact your account manager or support — we’ll enable it on your workspace within a
            business day.
          </div>
        </div>
        <Button variant="blue" onClick={() => navigate('/contact')}>
          <Icon name="arrowRight" size={14} />
          <span>Contact support</span>
        </Button>
      </Card>

      <LockedStyles />
    </Page>
  );
};

const LockedStyles: React.FC = () => (
  <style>{`
    .ai-accent {
      font-family: var(--font-disp);
      font-style: italic;
      font-weight: 400;
      color: var(--brand-blue);
      letter-spacing: -0.01em;
    }

    /* Stats share the chat-page design */
    .ai-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 14px;
      margin-bottom: 20px;
    }
    .ai-stat {
      background: var(--bg-elev);
      border: 1px solid var(--line);
      border-radius: var(--r-lg);
      padding: 18px 20px;
      display: flex; flex-direction: column; gap: 8px;
    }
    .ai-stat-label { font-size: 12px; font-weight: 500; color: var(--ink-3); }
    .ai-stat-row   { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; }
    .ai-stat-value {
      font-size: 26px; font-weight: 600; letter-spacing: -0.02em;
      color: var(--ink); line-height: 1; font-variant-numeric: tabular-nums;
    }
    .ai-stat-foot { font-size: 12px; color: var(--ink-3); padding-top: 2px; }

    /* Hero ribbon — same navy treatment as ov-whatsnew */
    .ai-hero {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      gap: 32px;
      align-items: center;
      background: var(--brand-navy);
      color: #fff;
      border-radius: var(--r-lg);
      padding: 34px 36px;
      overflow: hidden;
      position: relative;
      margin-bottom: 20px;
    }
    .ai-hero::before {
      content: "";
      position: absolute; inset: -30% -15% auto auto;
      width: 520px; height: 520px;
      background: radial-gradient(circle, rgba(25, 198, 230, 0.22) 0%, transparent 60%);
      pointer-events: none;
    }
    .ai-hero::after {
      content: "";
      position: absolute; left: -10%; bottom: -40%;
      width: 320px; height: 320px;
      background: radial-gradient(circle, rgba(93, 230, 182, 0.18) 0%, transparent 60%);
      pointer-events: none;
    }
    .ai-hero-text { position: relative; z-index: 1; }
    .ai-hero-text h3 {
      font-size: 30px;
      font-weight: 600;
      letter-spacing: -0.025em;
      color: #fff;
      line-height: 1.15;
      margin: 0;
    }
    .ai-hero-text h3 em {
      font-family: var(--font-disp);
      font-style: italic;
      font-weight: 400;
      background: linear-gradient(120deg, #19c6e6 0%, #5de6b6 60%, #ffb23f 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .ai-hero-text p {
      margin-top: 12px;
      font-size: 14px;
      color: #cfd1e4;
      line-height: 1.6;
      max-width: 480px;
    }

    .ai-hero-viz { position: relative; z-index: 1; }
    .ai-hero-chat {
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      padding: 16px;
      backdrop-filter: blur(6px);
      box-shadow: 0 24px 60px rgba(4, 8, 24, 0.45);
    }
    .ai-hero-head {
      display: flex; align-items: center; gap: 7px; margin-bottom: 14px;
    }
    .ai-hero-dot { width: 10px; height: 10px; border-radius: 999px; }
    .ai-hero-tag {
      font-family: var(--font-mono);
      font-size: 10px;
      color: #8d92af;
      margin-inline-start: auto;
      letter-spacing: 0.04em;
    }
    .ai-hero-bubble {
      padding: 9px 12px;
      border-radius: 12px;
      font-size: 13px;
      line-height: 1.5;
      max-width: 86%;
      margin-bottom: 8px;
    }
    .ai-hero-user {
      margin-inline-start: auto;
      background: rgba(43, 85, 255, 0.92);
      color: #fff;
      border-radius: 12px 12px 4px 12px;
    }
    .ai-hero-ai {
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #eceefa;
      border-radius: 12px 12px 12px 4px;
    }
    .ai-hero-rates {
      display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 6px 0 10px;
    }
    .ai-hero-rate {
      padding: 10px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
    }
    .ai-hero-rate-active {
      background: rgba(25, 198, 230, 0.10);
      border-color: rgba(25, 198, 230, 0.36);
    }
    .ai-hero-rate-label {
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #9aa0c0;
    }
    .ai-hero-rate-active .ai-hero-rate-label { color: #bff1fa; }
    .ai-hero-rate-value {
      margin-top: 4px;
      font-weight: 600;
      color: #e5e7f0;
    }
    .ai-hero-rate-active .ai-hero-rate-value { color: #fff; }
    .ai-hero-success {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(93, 230, 182, 0.10);
      border: 1px solid rgba(93, 230, 182, 0.32);
      font-size: 12px;
      color: #d9fbee;
      margin-top: 4px;
    }

    /* Feature grid */
    .ai-feature-grid {
      background: var(--bg-elev);
      border: 1px solid var(--line);
      border-radius: var(--r-lg);
      overflow: hidden;
      margin-bottom: 20px;
    }
    .ai-feature-head { padding: 18px 20px; border-bottom: 1px solid var(--line); }
    .ai-features {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0;
    }
    .ai-feature {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 14px;
      padding: 18px 20px;
      border-right: 1px dashed var(--line);
      border-bottom: 1px dashed var(--line);
    }
    .ai-feature:nth-child(2n) { border-right: none; }
    .ai-feature:nth-last-child(-n+2) { border-bottom: none; }
    .ai-feature-icon {
      width: 36px; height: 36px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, var(--brand-navy), var(--brand-blue));
      color: var(--brand-cyan);
      flex-shrink: 0;
    }
    .ai-feature-title { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 3px; }
    .ai-feature-body { font-size: 13px; color: var(--ink-3); line-height: 1.55; }

    /* CTA strip */
    .ai-cta {
      display: flex !important;
      gap: 14px;
      align-items: center;
      flex-wrap: wrap;
    }
    .ai-cta-icon {
      width: 40px; height: 40px; border-radius: 10px;
      background: linear-gradient(135deg, var(--brand-navy), var(--brand-blue));
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .ai-cta-text { flex: 1; min-width: 220px; }
    .ai-cta-title { font-size: 14px; font-weight: 600; color: var(--ink); }
    .ai-cta-sub   { font-size: 13px; color: var(--ink-3); margin-top: 2px; line-height: 1.5; }

    @media (max-width: 1100px) {
      .ai-stats { grid-template-columns: repeat(2, 1fr); }
      .ai-hero { grid-template-columns: 1fr; padding: 28px; }
      .ai-hero-text h3 { font-size: 26px; }
    }
    @media (max-width: 700px) {
      .ai-stats { grid-template-columns: 1fr 1fr; }
      .ai-features { grid-template-columns: 1fr; }
      .ai-feature { border-right: none !important; }
      .ai-feature:last-child { border-bottom: none; }
    }
  `}</style>
);

const AiShipmentPage: React.FC = () => {
  const { user } = useAuth();
  if (!user?.ai_shipment_enabled) return <LockedState />;
  return <AiShipmentChat />;
};

export default AiShipmentPage;
